import { useState, useEffect, useRef } from 'react'
import { getFragment, getContentFragment, isCoveoParam, isContentFacetParam } from '../shared/coveoUtils'
import { LISTING_PAGE_MARKER, LISTING_PAGE_FLAG } from '../constants'

const AUTOMATIC_FACET_PARAM_PREFIX = 'af-'

function getContentFacetKey(facet) {
  return facet?.state?.field || facet?.state?.facetId
}

/**
 * Combines the fixed checkbox-facet controllers with any relevance-driven automatic-facet
 * controllers the generator has produced so far. Both sets can carry independently
 * restorable af- selections (see contentEngine.js's buildUrlManager comment), so neither
 * can be dropped just because the other is present. Fields are deduplicated in favor of
 * the explicit (fixed) controller, since that's the one actually wired up for search.
 */
function getContentFacetControllers(contentFacets, contentFacetGenerator) {
  const explicitFacets = contentFacets || []
  const automaticFacets = contentFacetGenerator.state.automaticFacets || []
  return [
    ...explicitFacets,
    ...automaticFacets.filter((automaticFacet) => !explicitFacets.some(
      (explicitFacet) => getContentFacetKey(explicitFacet) === getContentFacetKey(automaticFacet)
    )),
  ]
}

function getSelectedContentFacetValue(breadcrumbValue) {
  if (typeof breadcrumbValue === 'string') return breadcrumbValue
  if (breadcrumbValue?.value != null) return String(breadcrumbValue.value)
  return null
}

function serializeContentFacetBreadcrumbs(contentBreadcrumbManager) {
  const params = new URLSearchParams()
  const breadcrumbs = [
    ...(contentBreadcrumbManager?.state?.facetBreadcrumbs || []),
    ...(contentBreadcrumbManager?.state?.automaticFacetBreadcrumbs || []),
  ]

  breadcrumbs.forEach((breadcrumb) => {
    const field = breadcrumb?.field || breadcrumb?.facetId
    if (!field) return

    const values = (breadcrumb.values || [])
      .map(({ value }) => getSelectedContentFacetValue(value))
      .filter(Boolean)

    if (!values.length) return
    params.set(`${AUTOMATIC_FACET_PARAM_PREFIX}${field}`, values.join(','))
  })

  return params
}

/**
 * Parses af-* entries out of a content fragment string into
 * [{ field, values: string[] }] pairs, ready to apply via toggleSelect.
 */
function parseAutomaticFacetEntries(fragment) {
  const params = new URLSearchParams(fragment)
  const entries = []
  for (const [key, rawValue] of params.entries()) {
    if (!key.startsWith(AUTOMATIC_FACET_PARAM_PREFIX)) continue
    const field = key.slice(AUTOMATIC_FACET_PARAM_PREFIX.length)
    const values = rawValue.split(',').filter(Boolean)
    if (values.length) entries.push({ field, values })
  }
  return entries
}

/**
 * Applies af-* URL entries onto currently generated automatic facet controllers
 * via toggleSelect — the same mechanism the interactive TopicTag → setContentFilter
 * flow uses. This is a fallback only: the content search box submits with
 * clearFilters: false, so the initial request already asks for these facets via
 * automaticFacetSet's URL-restored state, and the response normally reflects them as
 * already selected. This function only has work to do when a facet field isn't yet
 * among the response's relevance-driven candidates (revealed progressively).
 *
 * @returns {boolean} true if at least one entry was successfully applied
 */
function applyContentFacetEntries(contentFacetControllers, afEntries) {
  let appliedAny = false
  const facetsByField = new Map(contentFacetControllers.map((facet) => [getContentFacetKey(facet), facet]))

  afEntries.forEach(({ field, values }) => {
    const targetFacet = facetsByField.get(field)
    if (!targetFacet) return // Field isn't among the current relevance-driven candidates

    const valuesByValue = new Map(targetFacet.state.values.map((v) => [v.value, v]))
    values.forEach((val) => {
      const value = valuesByValue.get(val)
      if (!value) return // Value isn't among this facet's current candidates
      if (value.state !== 'selected') targetFacet.toggleSelect(value) // Also triggers the filtered search
      appliedAny = true
    })
  })
  return appliedAny
}

/** Stable signature of the current automatic-facet candidate fields, used to detect when
 * the candidate set has stopped changing so a permanently-unmatchable entry can be given up on. */
function getFacetFieldSignature(contentFacetControllers) {
  return contentFacetControllers.map((facet) => getContentFacetKey(facet)).filter(Boolean).sort().join(',')
}

/**
 * Manages URL ↔ Coveo state synchronisation and fires the initial search/listing request.
 *
 * Responsibilities:
 *  - Listens for browser popstate events and synchronises the Coveo URL manager(s).
 *  - Subscribes to the active (commerce) URL manager and the content (classic headless)
 *    URL manager, and pushes/replaces the browser history entry when either changes its
 *    internal state (pagination, sort, facets, etc.). Both tabs' facets are kept in the
 *    URL simultaneously — each manager only owns/touches its own param namespace
 *    (`f-`/`cf-`/... for commerce, `af-` for content) so they never clobber each other.
 *  - Subscribes to product and content facet generators to trigger re-renders when
 *    facet controllers are added or removed (facet IDs change).
 *  - Fires executeFirstSearch / executeFirstRequest on mount if not already done.
 *
 * @param {object} params
 * @param {object} params.activeCore                - Coveo search or productListing controller
 * @param {object} params.activeUrlManager          - URL manager for the active page type
 * @param {object} params.activeFacetGenerator      - FacetGenerator for the active page type
 * @param {object} params.contentFacetGenerator     - FacetGenerator for the content engine
 * @param {object} [params.contentUrlManager]       - URL manager for the content (classic headless) engine
 * @param {object} [params.contentBreadcrumbManager] - BreadcrumbManager for the content engine, used to
 *   clear stale automatic-facet selections before re-synchronizing from the URL
 * @param {object} [params.contentSearchBox]        - SearchBox for the content engine. Classic headless's
 *   urlManager.synchronize() (restoreSearchParameters) is a plain action — it does NOT trigger a new
 *   search — so contentSearchBox.submit() must be called afterward to actually fetch results
 *   reflecting the restored facet selections.
 * @param {boolean} params.isListingPage             - true on product-listing pages
 *
 * @returns {{ facetTick: number, contentFacetTick: number }}
 *   Tick counters that increment whenever facets change — consumers can ignore the
 *   values; they exist only to trigger re-renders via dependency arrays.
 */
export function useUrlSync({
  activeCore,
  activeUrlManager,
  activeFacetGenerator,
  contentFacets,
  contentFacetGenerator,
  contentUrlManager,
  contentBreadcrumbManager,
  contentSearchBox,
  isListingPage,
}) {
  const [facetTick, setFacetTick] = useState(0)
  const [contentFacetTick, setContentFacetTick] = useState(0)

  // Snapshot of the URL at the time of the last successful response.
  // Initialised to the current href so the very first load is always the baseline.
  const lastGoodUrlRef = useRef(typeof window === 'undefined' ? '' : window.location.href)

  // Guards the one-time re-synchronize once content automatic facets first load (see effect below).
  const appliedInitialContentFacetsRef = useRef(false)
  // Tracks the candidate-field signature from the last failed apply attempt, so we can
  // stop retrying once the candidate set stops changing between search responses.
  const lastAttemptedFacetSignatureRef = useRef(null)

  // Guards the commerce urlManager → URL sync until the first response lands. Commerce
  // facet IDs are also registered dynamically from the response, so the fragment right
  // after restore-from-URL but before that first response may not yet reflect the
  // restored selection — syncing early would wrongly strip the param from the address bar.
  const appliedInitialActiveFacetsRef = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Writes a urlManager's owned params (per isOwnedParam) from its fragment into the
    // address bar, without touching 'q' (owned by SearchPage) or any other manager's params.
    const syncFragmentToUrl = (urlManager, isOwnedParam, extraParams) => {
      const fragment = urlManager.state.fragment
      const coveoParams = new URLSearchParams(fragment)
      const url = new URL(window.location.href)

      // Remove stale params owned by this manager — never touch 'q'. A key must be removed
      // either because it's about to be overwritten (present in the new fragment) or
      // because it's owned by this manager but no longer present (e.g. cleared filters).
      const keysToRemove = []
      for (const key of url.searchParams.keys()) {
        if (key === 'q') continue
        if (isOwnedParam(key)) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach((k) => url.searchParams.delete(k))

      for (const [key, value] of coveoParams.entries()) {
        if (key === 'q') continue
        // Only write params this manager owns — e.g. the content urlManager's own
        // fragment serializes regular (manually-built) facet selections using their
        // native f-<field> form, which must not leak into the address bar since content
        // facets are only ever shown there as af-<field> (see extraParams below).
        if (!isOwnedParam(key)) continue
        url.searchParams.set(key, value)
      }

      if (extraParams) {
        for (const [key, value] of extraParams.entries()) {
          if (key === 'q') continue
          url.searchParams.set(key, value)
        }
      }
      if (url.toString() === window.location.href) return

      if (!activeCore.state.responseId) {
        window.history.replaceState(null, document.title, url.toString())
      } else {
        window.history.pushState(null, document.title, url.toString())
      }
    }

    // --- Browser back / forward ---
    const onPopState = () => {
      activeUrlManager.synchronize(getFragment())
      if (contentUrlManager) {
        appliedInitialContentFacetsRef.current = false
        lastAttemptedFacetSignatureRef.current = null
        // Automatic-facet selections aren't reliably cleared by synchronize() alone
        // (they're a dynamic/generated set, not fixed upfront) — deselect first for a clean slate.
        contentBreadcrumbManager?.deselectAll()
        const regularContentFacetFields = (contentFacets || []).map(getContentFacetKey).filter(Boolean)
        contentUrlManager.synchronize(getContentFragment(regularContentFacetFields))
        const afEntries = parseAutomaticFacetEntries(getContentFragment())
        const contentFacetControllers = getContentFacetControllers(contentFacets, contentFacetGenerator)
        if (afEntries.length && contentFacetControllers.length) {
          applyContentFacetEntries(contentFacetControllers, afEntries)
        }
        // synchronize() only restores parameter state — it does not re-fetch. Submit to
        // actually re-run the search with the restored facet selections applied.
        contentSearchBox?.submit()
      }
    }
    window.addEventListener('popstate', onPopState)

    // --- Coveo → URL sync ---
    const syncActiveUrlManager = () => syncFragmentToUrl(activeUrlManager, isCoveoParam)
    const syncContentUrlManager = () => syncFragmentToUrl(
      contentUrlManager,
      isContentFacetParam,
      serializeContentFacetBreadcrumbs(contentBreadcrumbManager)
    )

    const unsubUrlManager = activeUrlManager.subscribe(() => {
      if (!appliedInitialActiveFacetsRef.current) return
      syncActiveUrlManager()
    })
    const unsubContentUrlManager = contentUrlManager?.subscribe(() => {
      if (!appliedInitialContentFacetsRef.current) return
      syncContentUrlManager()
    })
    const unsubContentBreadcrumbManager = contentBreadcrumbManager?.subscribe?.(() => {
      if (!appliedInitialContentFacetsRef.current) return
      syncContentUrlManager()
    })

    // ── Core state subscription — revert URL on error ───────────────────────
    // The URL manager subscriber fires *before* the error lands on core.state,
    // so we watch core directly and revert once the error is confirmed.
    const unsubCore = activeCore.subscribe(() => {
      if (activeCore.state.error) {
        // API failed — restore the address bar to the last successful URL.
        window.history.replaceState(null, document.title, lastGoodUrlRef.current)
      } else if (activeCore.state.responseId) {
        // Successful response — record this URL as the new good baseline, and allow the
        // commerce urlManager sync now that its restored selection has settled.
        lastGoodUrlRef.current = window.location.href
        if (!appliedInitialActiveFacetsRef.current) {
          appliedInitialActiveFacetsRef.current = true
          // The urlManager's own restore-driven fragment change fired *before* this guard
          // opened (and was skipped) — sync once now so the restored facet isn't lost from
          // the address bar; nothing else will re-notify since no further state changed.
          syncActiveUrlManager()
        }
      }
    })

    // --- Facet subscriptions (tick counters force re-render when IDs change) ---
    const unsubFacetGen        = activeFacetGenerator.subscribe(() => setFacetTick((t) => t + 1))
    const unsubContentFacetGen = contentFacetGenerator.subscribe(() => setContentFacetTick((t) => t + 1))
    const contentFacetUnsubs = (contentFacets || [])
      .map((facet) => facet?.subscribe?.(() => setContentFacetTick((t) => t + 1)))
      .filter(Boolean)

    return () => {
      window.removeEventListener('popstate', onPopState)
      unsubUrlManager()
      unsubContentUrlManager?.()
      unsubContentBreadcrumbManager?.()
      unsubCore()
      unsubFacetGen()
      unsubContentFacetGen()
      contentFacetUnsubs.forEach((unsub) => unsub())
    }
  }, [activeCore, activeUrlManager, activeFacetGenerator, isListingPage, contentFacets, contentFacetGenerator, contentUrlManager, contentBreadcrumbManager, contentSearchBox]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!contentUrlManager) return
    if (appliedInitialContentFacetsRef.current) return
    const contentFacetControllers = getContentFacetControllers(contentFacets, contentFacetGenerator)
    if (!contentFacetControllers.length) return

    const afEntries = parseAutomaticFacetEntries(getContentFragment())
    if (!afEntries.length) {
      appliedInitialContentFacetsRef.current = true
      return
    }

    const facetsByField = new Map(contentFacetControllers.map((facet) => [getContentFacetKey(facet), facet]))
    const isEntryResolved = ({ field, values }) => {
      const targetFacet = facetsByField.get(field)
      if (!targetFacet) return false
      const valuesByValue = new Map(targetFacet.state.values.map((v) => [v.value, v]))
      return values.every((val) => valuesByValue.get(val)?.state === 'selected')
    }

    // Fast path: the content search box now submits with clearFilters: false, so the very
    // first request already asks for these facets (via automaticFacetSet's URL-restored
    // placeholders) and the response normally echoes them back as 'selected' already — in
    // which case there's nothing to toggle and we can skip applyAutomaticFacetEntries entirely.
    const allResolvedBeforeApply = afEntries.every(isEntryResolved)
    if (allResolvedBeforeApply) {
      appliedInitialContentFacetsRef.current = true
      return
    }

    // Fallback: some entries aren't reflected yet — e.g. a field wasn't among this
    // response's relevance-driven candidates and is only revealed by a later response
    // (possibly triggered by the other facets' toggleSelect calls below). Apply what we
    // can now and keep retrying on later ticks until everything resolves or the candidate
    // set stops changing.
    const appliedAny = applyContentFacetEntries(contentFacetControllers, afEntries)

    // If nothing was toggled, state can't have changed since allResolvedBeforeApply already
    // found it unresolved — skip re-checking and go straight to the retry/give-up logic below.
    const allResolvedAfterApply = appliedAny && afEntries.every(isEntryResolved)
    if (allResolvedAfterApply) {
      appliedInitialContentFacetsRef.current = true
      return
    }

    const currentSignature = getFacetFieldSignature(contentFacetControllers)
    if (currentSignature === lastAttemptedFacetSignatureRef.current) {
      // Candidate fields haven't changed since the last failed attempt —
      // the remaining field/value isn't among today's automatic facets. Stop.
      appliedInitialContentFacetsRef.current = true
    } else {
      lastAttemptedFacetSignatureRef.current = currentSignature
    }
  }, [contentFacetTick, contentFacets, contentUrlManager, contentFacetGenerator])

  // ─── Persist the listing-page marker into the URL ───────────────────────
  // Once a page has been resolved as a listing page (see coveoUtils.js's
  // resolveIsListingPage), write the isListingPage=true marker into the address bar so
  // it survives subsequent navigations/reloads that might otherwise fall outside the
  // bootstrap-only-params check (e.g. after filters are applied).
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (isListingPage) {
      const url = new URL(window.location.href)
      if (url.searchParams.get(LISTING_PAGE_MARKER) !== 'true') {
        url.searchParams.set(LISTING_PAGE_MARKER, 'true')
        const nextUrl = url.toString()
        window.history.replaceState(null, document.title, nextUrl)
        lastGoodUrlRef.current = nextUrl
      }
      window[LISTING_PAGE_FLAG] = true
    }
  }, [isListingPage])

  return { facetTick, contentFacetTick }
}
